import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { useTranslation } from 'react-i18next'

import type { LegalDocId } from '@redevicer/shared'

import LanguageSelect from './LanguageSelect'
import BrandWordmark from './BrandWordmark'
import HeaderStoreBadges from './HeaderStoreBadges'
import { IconBell, IconCart, IconHeart, IconReport, IconUser } from './AppNavIcons'
import {
  HeaderNavIconBuy,
  HeaderNavIconAccount,
  HeaderNavIconCheck,
  HeaderNavIconSell,
} from './HeaderNavMenuIcons'

import ProfileDropdown from './ProfileDropdown'



interface AppHeaderProps {

  onLogo: () => void

  onBuy: () => void

  onCheck: () => void

  onSell: () => void

  onSellFast?: () => void

  onOpenFavorites: () => void

  onOpenCart: () => void

  onOpenNotifications: () => void

  onOpenReports?: () => void

  notificationsUnreadCount?: number

  reportsUnreadCount?: number

  cartCount: number

  /** When false, hide cart icon (payments disabled). */
  showCart?: boolean

  onOpenAccount: () => void

  favoritesCount: number

  profileMenuOpen: boolean

  setProfileMenuOpen: (open: boolean) => void

  profileDisplayName: string

  profileEmail?: string

  isAdmin: boolean

  onSeeProfile: () => void

  onProfileMyListings: () => void

  onProfileMyOffers: () => void

  onProfileMyChats: () => void

  onProfileMyReports: () => void

  onProfileExamples: () => void

  onProfileAdmin?: () => void

  onProfileLogout: () => void

  onProfileLegal: (docId: LegalDocId) => void

  /** Cart — visible for guests too; favorites/notifications need login */

  isLoggedIn: boolean

  /** When true, profile menu is sign-in only */

  isGuest?: boolean

  onGuestLogin?: () => void

  /** Language selector */
  language?: string
  onLanguage?: (lng: string) => void

  /** Desktop sell flow: centered model search in navbar */
  centerSlot?: ReactNode

}



export default function AppHeader({

  onLogo,

  onBuy,

  onCheck,

  onSell,

  onSellFast,

  onOpenFavorites,

  onOpenCart,

  onOpenNotifications,

  onOpenReports,

  notificationsUnreadCount = 0,

  reportsUnreadCount = 0,

  onOpenAccount,

  favoritesCount,

  cartCount,

  showCart = true,

  profileMenuOpen,

  setProfileMenuOpen,

  profileDisplayName,

  profileEmail,

  isAdmin,

  onSeeProfile,

  onProfileMyListings,

  onProfileMyOffers,

  onProfileMyChats,

  onProfileMyReports,

  onProfileExamples,

  onProfileAdmin,

  onProfileLogout,

  onProfileLegal,

  isLoggedIn,

  isGuest,

  onGuestLogin,

  language,

  onLanguage,

  centerSlot,

}: AppHeaderProps) {

  const { t } = useTranslation()

  const [isDesktop, setIsDesktop] = useState(

    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 961px)').matches,

  )

  const [scrolled, setScrolled] = useState(false)

  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const mobileMenuBtnRef = useRef<HTMLButtonElement>(null)
  const mobileMenuPositionClearRef = useRef<number | null>(null)
  const mobileMenuScrollLockYRef = useRef(0)

  /** Matches `.vc-header-mobile-sheet` close transition (visibility delay included). */
  const MOBILE_MENU_SHEET_CLOSE_MS = 360

  function cancelScheduledMobileMenuPositionClear() {
    if (mobileMenuPositionClearRef.current == null) return
    window.clearTimeout(mobileMenuPositionClearRef.current)
    mobileMenuPositionClearRef.current = null
  }

  function scheduleMobileMenuPositionClear() {
    cancelScheduledMobileMenuPositionClear()
    mobileMenuPositionClearRef.current = window.setTimeout(() => {
      mobileMenuPositionClearRef.current = null
      clearMobileMenuSheetPosition()
    }, MOBILE_MENU_SHEET_CLOSE_MS)
  }

  function syncMobileMenuSheetPosition() {
    const btn = mobileMenuBtnRef.current
    if (!btn) return
    const headerEl = btn.closest('header')
    const headerRect =
      headerEl instanceof HTMLElement ? headerEl.getBoundingClientRect() : btn.getBoundingClientRect()
    const btnRect = btn.getBoundingClientRect()
    const root = document.documentElement
    /** Below full header chrome — not just the menu button (avoids overlapping the bar). */
    const sheetGapPx = 12
    root.style.setProperty('--vc-mobile-menu-sheet-top', `${Math.round(headerRect.bottom + sheetGapPx)}px`)
    root.style.setProperty('--vc-mobile-menu-sheet-right', `${Math.round(window.innerWidth - btnRect.right)}px`)
  }

  function clearMobileMenuSheetPosition() {
    const root = document.documentElement
    root.style.removeProperty('--vc-mobile-menu-sheet-top')
    root.style.removeProperty('--vc-mobile-menu-sheet-right')
  }

  function pinMobileHeaderForMenu(headerEl: HTMLElement) {
    const lockScrollY = window.scrollY
    const rect = headerEl.getBoundingClientRect()
    const headerStyle = window.getComputedStyle(headerEl)
    const marginTop = Number.parseFloat(headerStyle.marginTop) || 0
    const marginBottom = Number.parseFloat(headerStyle.marginBottom) || 0
    const spacerHeight = headerEl.offsetHeight + marginTop + marginBottom

    headerEl.classList.add('vc-header--menu-pinned')
    headerEl.style.position = 'fixed'
    headerEl.style.top = `${rect.top}px`
    headerEl.style.left = `${rect.left}px`
    headerEl.style.width = `${rect.width}px`
    headerEl.style.right = 'auto'
    headerEl.style.margin = '0'
    headerEl.style.zIndex = '54'

    let menuOpenSpacer: HTMLDivElement | null = null
    if (headerEl.parentElement) {
      menuOpenSpacer = document.createElement('div')
      menuOpenSpacer.className = 'vc-header-menu-open-spacer'
      menuOpenSpacer.setAttribute('aria-hidden', 'true')
      menuOpenSpacer.style.height = `${spacerHeight}px`
      headerEl.parentElement.insertBefore(menuOpenSpacer, headerEl)
    }

    if (window.scrollY !== lockScrollY) {
      window.scrollTo(0, lockScrollY)
    }

    return { menuOpenSpacer, lockScrollY }
  }

  function unpinMobileHeaderForMenu(headerEl: HTMLElement, menuOpenSpacer: HTMLDivElement | null) {
    headerEl.classList.remove('vc-header--menu-pinned')
    headerEl.style.removeProperty('position')
    headerEl.style.removeProperty('top')
    headerEl.style.removeProperty('left')
    headerEl.style.removeProperty('width')
    headerEl.style.removeProperty('right')
    headerEl.style.removeProperty('margin')
    headerEl.style.removeProperty('z-index')
    menuOpenSpacer?.remove()
  }

  useLayoutEffect(() => {
    if (isDesktop) return

    if (!mobileNavOpen) {
      scheduleMobileMenuPositionClear()
      return
    }

    cancelScheduledMobileMenuPositionClear()

    const headerEl = mobileMenuBtnRef.current?.closest('header')
    let menuOpenSpacer: HTMLDivElement | null = null
    let lockScrollY = window.scrollY
    if (headerEl instanceof HTMLElement) {
      const pinned = pinMobileHeaderForMenu(headerEl)
      menuOpenSpacer = pinned.menuOpenSpacer
      lockScrollY = pinned.lockScrollY
    }
    mobileMenuScrollLockYRef.current = lockScrollY

    syncMobileMenuSheetPosition()

    function preventBackgroundTouchScroll(ev: TouchEvent) {
      const target = ev.target
      if (target instanceof Element && target.closest('#vc-header-mobile-nav, .app-header.vc-header')) {
        return
      }
      ev.preventDefault()
    }

    function preventBackgroundWheel(ev: WheelEvent) {
      ev.preventDefault()
    }

    function preventBackgroundScroll() {
      if (window.scrollY !== mobileMenuScrollLockYRef.current) {
        window.scrollTo(0, mobileMenuScrollLockYRef.current)
      }
    }

    function onViewportChange() {
      syncMobileMenuSheetPosition()
    }

    document.addEventListener('touchmove', preventBackgroundTouchScroll, { passive: false })
    window.addEventListener('wheel', preventBackgroundWheel, { passive: false })
    window.addEventListener('scroll', preventBackgroundScroll, { passive: true })
    window.addEventListener('resize', onViewportChange, { passive: true })
    return () => {
      document.removeEventListener('touchmove', preventBackgroundTouchScroll)
      window.removeEventListener('wheel', preventBackgroundWheel)
      window.removeEventListener('scroll', preventBackgroundScroll)
      window.removeEventListener('resize', onViewportChange)
      if (headerEl instanceof HTMLElement) {
        unpinMobileHeaderForMenu(headerEl, menuOpenSpacer)
        window.scrollTo(0, lockScrollY)
      }
    }
  }, [mobileNavOpen, isDesktop])

  useEffect(() => () => cancelScheduledMobileMenuPositionClear(), [])

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])



  useEffect(() => {

    const mq = window.matchMedia('(min-width: 961px)')

    function sync() {

      setIsDesktop(mq.matches)

      if (!mq.matches) {
        setProfileMenuOpen(false)
        setMobileNavOpen(false)
      }

    }

    sync()

    mq.addEventListener('change', sync)

    return () => mq.removeEventListener('change', sync)

  }, [setProfileMenuOpen])

  useEffect(() => {
    if (!mobileNavOpen) return
    function onKeyDown(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setMobileNavOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [mobileNavOpen])

  function closeMobileNav() {
    setMobileNavOpen(false)
  }

  function runMobileNav(action: () => void) {
    closeMobileNav()
    action()
  }



  function handleProfileToggle() {

    if (isDesktop) setProfileMenuOpen(!profileMenuOpen)

    else {

      setProfileMenuOpen(false)

      onOpenAccount()

    }

  }



  return (
    <>
    <header
      className={`app-header vc-header${isLoggedIn ? '' : ' vc-header--guest'}${scrolled ? ' vc-header--scrolled' : ' vc-header--top'}${centerSlot ? ' vc-header--with-center' : ''}${mobileNavOpen && !isDesktop ? ' vc-header--menu-open' : ''}`}
    >

      <div className="vc-header-grid">

        <div className="vc-header-brand">

          <div className="vc-header-brand-lockup">
            <button type="button" className="header-logo-btn" onClick={onLogo} aria-label="reDevicer">
              <BrandWordmark className="brand-wordmark--header" title="" />
            </button>
            <HeaderStoreBadges />
          </div>

        </div>

        {centerSlot ? <div className="vc-header-center">{centerSlot}</div> : null}

        <div className="vc-header-actions">

          {onLanguage ? (
            <div className="vc-header-lang" aria-label={t('vc.language', 'Language')}>
              <LanguageSelect
                value={language || 'lt'}
                onChange={onLanguage}
                className="vc-header-lang-select"
                variant="icon"
              />
            </div>
          ) : null}

          {onSellFast ? (
            <button type="button" className="vc-header-sell vc-header-sell--fast" onClick={onSellFast}>
              {t('vc.headerSellFast')}
            </button>
          ) : null}

          <button type="button" className="vc-header-sell" onClick={onSell}>

            {t('vc.headerSell')}

          </button>

          <div

            className={`vc-header-quick${isLoggedIn ? ' vc-header-quick--member' : ''}`}

            aria-label={t('vc.quickActionsAria')}

          >

            {!isDesktop ? (
              <button
                ref={mobileMenuBtnRef}
                type="button"
                className={`vc-icon-btn vc-header-menu-btn${mobileNavOpen ? ' vc-header-menu-btn--open' : ''}`}
                aria-expanded={mobileNavOpen}
                aria-controls="vc-header-mobile-nav"
                aria-label={mobileNavOpen ? t('vc.headerNav.toggleClose') : t('vc.headerNav.toggleOpen')}
                onClick={() => {
                  setMobileNavOpen((open) => {
                    const next = !open
                    if (next) {
                      requestAnimationFrame(() => syncMobileMenuSheetPosition())
                    }
                    return next
                  })
                }}
              >
                <span className="vc-hamburger" aria-hidden="true">
                  <span className="vc-hamburger__bar" />
                  <span className="vc-hamburger__bar" />
                  <span className="vc-hamburger__bar" />
                </span>
              </button>
            ) : null}

            <button
              type="button"
              className="vc-icon-btn vc-header-only-desktop vc-header-favorites"
              onClick={onOpenFavorites}
              aria-label={t('vc.tab.favorites')}
            >
              <IconHeart />
              {favoritesCount > 0 ? (
                <span className="vc-icon-badge vc-icon-badge--fav">{favoritesCount > 99 ? '99+' : favoritesCount}</span>
              ) : null}
            </button>

            {isLoggedIn && onOpenReports ? (
              <button
                type="button"
                className="vc-icon-btn vc-header-only-desktop vc-icon-btn--reports"
                onClick={onOpenReports}
                aria-label={t('account.tabReports')}
              >
                <IconReport />
                {reportsUnreadCount > 0 ? (
                  <span className="vc-notif-badge" aria-hidden="true">
                    {reportsUnreadCount > 9 ? '9+' : reportsUnreadCount}
                  </span>
                ) : null}
              </button>
            ) : null}

            {isLoggedIn ? (
              <button
                type="button"
                className="vc-icon-btn vc-header-only-desktop vc-icon-btn--notif"
                onClick={onOpenNotifications}
                aria-label={t('vc.tab.notifications')}
              >
                <IconBell />
                {notificationsUnreadCount > 0 ? (
                  <span className="vc-notif-badge" aria-hidden="true">
                    {notificationsUnreadCount > 9 ? '9+' : notificationsUnreadCount}
                  </span>
                ) : null}
              </button>
            ) : null}

            {showCart ? (
            <button type="button" className="vc-icon-btn" onClick={onOpenCart} aria-label={t('vc.tab.cart')}>
              <IconCart />
              {cartCount > 0 && (
                <span className="vc-icon-badge">{cartCount > 99 ? '99+' : cartCount}</span>
              )}
            </button>
            ) : null}

            <div className="vc-profile-anchor">
              <button
                type="button"
                className={`vc-icon-btn vc-header-only-desktop${profileMenuOpen && isDesktop ? ' vc-icon-btn--active' : ''}`}
                onClick={handleProfileToggle}
                aria-expanded={isDesktop ? profileMenuOpen : undefined}
                aria-haspopup={isDesktop ? 'dialog' : undefined}
                aria-label={t('vc.tab.me')}
              >
                <IconUser />
              </button>
              {isDesktop && (
                <ProfileDropdown
                  isOpen={profileMenuOpen}
                  onClose={() => setProfileMenuOpen(false)}
                  displayName={profileDisplayName}
                  email={profileEmail}
                  isAdmin={isGuest ? false : isAdmin}
                  onSeeProfile={onSeeProfile}
                  onMyListings={onProfileMyListings}
                  onMyOffers={onProfileMyOffers}
                  onMyChats={onProfileMyChats}
                  onMyReports={onProfileMyReports}
                  onExamples={onProfileExamples}
                  onAdmin={onProfileAdmin}
                  onLogout={onProfileLogout}
                  onOpenLegal={onProfileLegal}
                  guest={isGuest}
                  onLogin={onGuestLogin}
                />
              )}
            </div>

          </div>

        </div>

      </div>

    </header>

    {!isDesktop && typeof document !== 'undefined'
      ? createPortal(
          <>
            <button
              type="button"
              className={`vc-header-mobile-backdrop${mobileNavOpen ? ' vc-header-mobile-backdrop--open' : ''}`}
              aria-hidden={!mobileNavOpen}
              tabIndex={mobileNavOpen ? 0 : -1}
              aria-label={t('vc.headerNav.toggleClose')}
              onClick={closeMobileNav}
            />
            <nav
              id="vc-header-mobile-nav"
              className={`vc-header-mobile-sheet${mobileNavOpen ? ' vc-header-mobile-sheet--open' : ''}`}
              aria-label={t('vc.headerNav.menuAria')}
              aria-hidden={!mobileNavOpen}
            >
              <button type="button" className="vc-header-mobile-link" onClick={() => runMobileNav(onBuy)}>
                <span className="vc-header-mobile-link__start">
                  <span className="vc-header-mobile-link__icon" aria-hidden="true">
                    <HeaderNavIconBuy />
                  </span>
                  <span className="vc-header-mobile-link__label">{t('vc.headerNav.buy')}</span>
                </span>
                <span className="vc-header-mobile-link__chev" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="vc-header-mobile-link vc-header-mobile-link--accent"
                onClick={() => runMobileNav(onSell)}
              >
                <span className="vc-header-mobile-link__start">
                  <span className="vc-header-mobile-link__icon" aria-hidden="true">
                    <HeaderNavIconSell />
                  </span>
                  <span className="vc-header-mobile-link__label">{t('vc.headerNav.sell')}</span>
                </span>
                <span className="vc-header-mobile-link__chev" aria-hidden="true" />
              </button>
              <button type="button" className="vc-header-mobile-link" onClick={() => runMobileNav(onCheck)}>
                <span className="vc-header-mobile-link__start">
                  <span className="vc-header-mobile-link__icon" aria-hidden="true">
                    <HeaderNavIconCheck />
                  </span>
                  <span className="vc-header-mobile-link__label">{t('vc.headerNav.check')}</span>
                </span>
                <span className="vc-header-mobile-link__chev" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="vc-header-mobile-link"
                onClick={() => {
                  closeMobileNav()
                  if (isGuest && onGuestLogin) onGuestLogin()
                  else onOpenAccount()
                }}
              >
                <span className="vc-header-mobile-link__start">
                  <span className="vc-header-mobile-link__icon" aria-hidden="true">
                    <HeaderNavIconAccount />
                  </span>
                  <span className="vc-header-mobile-link__label">
                    {isGuest ? t('auth.login') : t('vc.tab.me')}
                  </span>
                </span>
                <span className="vc-header-mobile-link__chev" aria-hidden="true" />
              </button>
              {onLanguage ? (
                <div className="vc-header-mobile-sheet__lang" aria-label={t('vc.language', 'Language')}>
                  <span className="vc-header-mobile-sheet__lang-label">{t('vc.language', 'Language')}</span>
                  <LanguageSelect
                    value={language || 'lt'}
                    onChange={onLanguage}
                    className="vc-header-mobile-lang-select"
                    variant="full"
                  />
                </div>
              ) : null}
            </nav>
          </>,
          document.body,
        )
      : null}
    </>
  )

}


